import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { FiGlobe, FiChevronDown, FiSearch, FiX, FiCheck } from 'react-icons/fi';
import { useTimezonesQuery, timezoneOptionLabel, type Timezone } from '../../api/timezones';

interface TimezoneSelectProps {
  value: string;
  onChange: (value: string) => void;
}

function isGenericUtcTimezone(value?: string | null): boolean {
  if (!value) return true;
  const trimmed = value.trim();
  return trimmed === 'UTC' || trimmed === 'Etc/UTC' || trimmed === 'Etc/GMT' || trimmed === 'GMT';
}

function ianaCityLabel(iana: string): string {
  const city = iana.split('/').pop() ?? iana;
  return city.replace(/_/g, ' ');
}

/** Match query against the label, IANA id, city name or country. */
function entryMatchesQuery(tz: Timezone, query: string): boolean {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  return (
    timezoneOptionLabel(tz).toLowerCase().includes(q) ||
    tz.name.toLowerCase().includes(q) ||
    ianaCityLabel(tz.name).toLowerCase().includes(q) ||
    tz.utc_offset.includes(q) ||
    tz.countries.some((c) => c.toLowerCase().includes(q))
  );
}

export const TimezoneSelect: React.FC<TimezoneSelectProps> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const keyBuffer = useRef('');
  const keyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: catalog, isLoading, isError, refetch, isFetching } = useTimezonesQuery();

  /**
   * Backend catalog, plus the currently selected value when it is not in the catalog
   * (legacy event, or a browser default) so a saved timezone is never silently replaced.
   */
  const options = useMemo<Timezone[]>(() => {
    const list = catalog ?? [];
    const current = value?.trim();
    if (!current || list.some((tz) => tz.name.toLowerCase() === current.toLowerCase())) return list;
    const extra: Timezone = {
      id: -1,
      name: current,
      display_name: ianaCityLabel(current),
      utc_offset: '',
      gmt_label: '',
      countries: [],
      sort_order: -1,
    };
    return [extra, ...list];
  }, [catalog, value]);

  const labelFor = (tz: Timezone) => (tz.gmt_label ? timezoneOptionLabel(tz) : tz.name);

  const filteredTimezones = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter((tz) => entryMatchesQuery(tz, search));
  }, [options, search]);

  const isSelected = (tz: Timezone) => tz.name.toLowerCase() === (value ?? '').trim().toLowerCase();

  const selectedLabel = useMemo(() => {
    const entry = options.find(isSelected);
    return entry ? labelFor(entry) : value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 0);
      const idx = filteredTimezones.findIndex(isSelected);
      if (idx > -1 && listRef.current) {
        const item = listRef.current.children[idx] as HTMLElement;
        item?.scrollIntoView({ block: 'nearest' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
      return;
    }
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey) return;
    keyBuffer.current += e.key.toLowerCase();
    if (keyTimer.current) clearTimeout(keyTimer.current);
    keyTimer.current = setTimeout(() => {
      keyBuffer.current = '';
    }, 800);
    const buf = keyBuffer.current;
    const idx = filteredTimezones.findIndex(
      (tz) =>
        tz.display_name.toLowerCase().startsWith(buf) ||
        ianaCityLabel(tz.name).toLowerCase().startsWith(buf),
    );
    if (idx > -1 && listRef.current) {
      const item = listRef.current.children[idx] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
      item?.focus();
    }
  };

  const handleSelect = useCallback(
    (iana: string) => {
      onChange(iana);
      setOpen(false);
      setSearch('');
    },
    [onChange],
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-14 bg-zinc-900/40 border border-white/25 rounded-2xl pl-11 pr-10 text-white text-sm text-left flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all cursor-pointer"
      >
        <span className="truncate">{selectedLabel}</span>
      </button>
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none">
        <FiGlobe size={16} />
      </div>
      <FiChevronDown
        className={`absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none transition-transform ${open ? 'rotate-180' : ''}`}
      />

      {open && (
        <div className="absolute z-50 mt-2 w-full rounded-2xl border border-white/10 bg-zinc-900 shadow-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
            <FiSearch size={14} className="text-zinc-500 shrink-0" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search timezone…"
              className="flex-1 bg-transparent text-sm text-white placeholder-zinc-500 focus:outline-none"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="text-zinc-500 hover:text-white"
              >
                <FiX size={14} />
              </button>
            )}
          </div>
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-zinc-500">Loading timezones…</div>
          ) : isError && options.length === 0 ? (
            <div className="px-4 py-3 text-sm text-red-400 flex items-center justify-between gap-3">
              <span>Failed to load timezones.</span>
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
                className="text-white underline disabled:opacity-50"
              >
                Retry
              </button>
            </div>
          ) : (
            <ul
              ref={listRef}
              onKeyDown={handleListKeyDown}
              className="max-h-60 overflow-y-auto py-1"
            >
              {filteredTimezones.length === 0 ? (
                <li className="px-4 py-3 text-sm text-zinc-500">
                  {options.length === 0 ? 'No timezones available' : 'No results'}
                </li>
              ) : (
                filteredTimezones.map((tz) => {
                  const selected = isSelected(tz);
                  return (
                    <li
                      key={tz.name}
                      tabIndex={0}
                      onClick={() => handleSelect(tz.name)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') handleSelect(tz.name);
                      }}
                      className={`flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer transition-colors focus:outline-none ${
                        selected
                          ? 'bg-blue-600 text-white'
                          : 'text-zinc-300 hover:bg-white/5 focus:bg-white/8'
                      }`}
                    >
                      <span>{labelFor(tz)}</span>
                      {selected && <FiCheck size={14} className="shrink-0 ml-2" />}
                    </li>
                  );
                })
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

/** Browser IANA timezone, resolved at call time (prefer over module-load defaults). */
export function getUserTimezone(): string {
  try {
    const tz =
      typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined;
    if (tz && !isGenericUtcTimezone(tz)) return tz;
    // Intl sometimes reports bare "UTC" (SSR / misconfigured hosts). Prefer a concrete IANA id.
    if (tz === 'UTC' || tz === 'Etc/UTC') return 'Etc/GMT';
    if (tz) return tz;
  } catch {
    // ignore
  }
  return 'America/New_York';
}

/**
 * Prefer a stored offer/event timezone when it looks intentional.
 * Generic UTC placeholders from the API fall back to the browser timezone.
 */
export function resolveDefaultTimezone(stored?: string | null): string {
  if (stored && !isGenericUtcTimezone(stored)) return stored.trim();
  return getUserTimezone();
}

/** Prefer `getUserTimezone()` / `resolveDefaultTimezone()` when initializing form state. */
export const DEFAULT_TIMEZONE = getUserTimezone();

export { isGenericUtcTimezone };
